import React, { Fragment, useCallback, useState } from 'react';

import { PrimaryButton, SecondaryButton, TertiaryButton } from '@rbilabs/components-library';
import Carousel, { CarouselSlideRenderControlProps } from 'nuka-carousel';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { ThemeProvider } from 'styled-components';

import { Nullable } from '@rbi-ctg/frontend';
import { IBaseItem, ICartEntry, IItem } from '@rbi-ctg/menu';
import Picture from 'components/picture';
import { IAddOnSection, IAddOnSectionOption } from 'hooks/use-cart-add-ons';
import useMediaQuery from 'hooks/use-media-query';
import { theme as componentLibraryTheme } from 'styles/configure-theme';

import { useAddonsFreeModal } from './hook';
import {
  ContentHeader,
  DescriptionModal,
  DescriptionModalWrapper,
  ImageContainer,
  ModalContainer,
  ModalWrapper,
  NextButton,
  PreviousButton,
  TipMessage,
  TitleModal,
  TitleModalWrapper,
} from './styled';

interface IAddOnsFreeModal {
  cartEntries: ICartEntry[];
  addOnSections: IAddOnSection[];
  addItemToCart: (items: IBaseItem) => void;
}

export const AddOnFreeModal = ({ addItemToCart, addOnSections, cartEntries }: IAddOnsFreeModal) => {
  const { formatMessage } = useIntl();
  const { modalOptions, setModalOptions, hasModalWithOffer, showModal, setShowModal, hasOneItem } =
    useAddonsFreeModal({
      addOnSections,
      cartEntries,
    });
  const [currentIndex, setCurrentIndex] = useState(0);
  const isMobile = useMediaQuery('mobile');

  const closeModal = useCallback(() => {
    setShowModal(false);
    setModalOptions([]);
  }, [setModalOptions, setShowModal]);

  const handleNotNow = useCallback(() => {
    const isLastSlide = modalOptions.length === currentIndex + 1;
    if (hasOneItem || isLastSlide) {
      closeModal();
      return;
    }

    setCurrentIndex(currentIndex + 1);
  }, [currentIndex, hasOneItem, modalOptions.length, closeModal]);

  const handlePrimaryButton = useCallback(
    (option: Nullable<IItem>) => {
      if (!option) {
        return;
      }

      if (hasOneItem) {
        addItemToCart(option);
        closeModal();
        return;
      }

      setCurrentIndex(currentIndex + 1);
      addItemToCart(option);
    },
    [hasOneItem, addItemToCart, currentIndex, closeModal]
  );

  const freeOptionIsInCart = useCallback(
    (freeOption: Nullable<IItem>) => {
      const isInCart = cartEntries.some(entry => entry._id === freeOption?._id);

      return isInCart;
    },
    [cartEntries]
  );

  if (!hasModalWithOffer || !showModal) {
    return null;
  }

  const renderPreviousButton = ({
    previousSlide,
    currentSlide,
  }: CarouselSlideRenderControlProps) => (
    <PreviousButton
      onClick={previousSlide}
      hidden={currentSlide === 0}
      aria-label={formatMessage({ id: 'backwardButton' })}
    />
  );

  const renderNextButton = ({
    nextSlide,
    slideCount,
    currentSlide,
  }: CarouselSlideRenderControlProps) => (
    <NextButton
      onClick={nextSlide}
      hidden={currentSlide + 1 === slideCount}
      aria-label={formatMessage({ id: 'forwardButton' })}
    />
  );

  const renderModalContent = ({
    _key,
    addOnSectionFreeModalInfos,
    freeOption,
  }: IAddOnSectionOption) => {
    const { image, primaryButtonText, title, description } = addOnSectionFreeModalInfos || {};

    const sanityImage = image?.locale?.app ?? freeOption?.image;
    const imageAlt = image?.locale?.imageDescription ?? '';

    const isAdded = freeOptionIsInCart(freeOption);
    const hasDescription = description !== null;

    return (
      <Fragment key={_key}>
        <ContentHeader>
          <ImageContainer>
            {sanityImage && <Picture image={sanityImage} alt={imageAlt} objectFitContain />}
          </ImageContainer>
        </ContentHeader>
        <TitleModalWrapper>
          <TitleModal aria-label={title?.locale} title={title?.locale}>
            {title?.locale}
          </TitleModal>
        </TitleModalWrapper>
        <DescriptionModalWrapper hasOneItem={hasOneItem} hasDescription={hasDescription}>
          <DescriptionModal aria-label={description?.locale} title={description?.locale}>
            {description?.locale}
          </DescriptionModal>
        </DescriptionModalWrapper>
        <PrimaryButton
          fullWidth
          aria-label={primaryButtonText?.locale}
          onClick={() => handlePrimaryButton(freeOption)}
          disabled={isAdded}
        >
          {!isAdded ? primaryButtonText?.locale : formatMessage({ id: 'addedToOrder' })}
        </PrimaryButton>
        {!isAdded ? (
          <SecondaryButton
            fullWidth
            aria-label={formatMessage({ id: 'notNow' })}
            onClick={handleNotNow}
          >
            {formatMessage({ id: 'notNow' })}
          </SecondaryButton>
        ) : (
          <TipMessage>{formatMessage({ id: 'YouCanRemoveThisItemLater' })}</TipMessage>
        )}
        {!hasOneItem && (
          <TertiaryButton
            fullWidth
            aria-label={formatMessage({ id: 'close' })}
            onClick={closeModal}
          >
            {formatMessage({ id: 'close' })}
          </TertiaryButton>
        )}
      </Fragment>
    );
  };

  const renderModal = () => {
    return (
      <ThemeProvider theme={componentLibraryTheme}>
        <ModalWrapper>
          <ModalContainer hasOneItem={hasOneItem}>
            <Carousel
              slideIndex={currentIndex}
              afterSlide={newIndex => setCurrentIndex(newIndex)}
              renderCenterLeftControls={!isMobile ? renderPreviousButton : null}
              renderCenterRightControls={!isMobile ? renderNextButton : null}
              withoutControls={hasOneItem}
              dragging={!hasOneItem}
              swiping={!hasOneItem}
              defaultControlsConfig={{
                pagingDotsStyle: {
                  top: isMobile ? '8px' : 0,
                  position: 'relative',
                },
              }}
            >
              {modalOptions.map(renderModalContent)}
            </Carousel>
          </ModalContainer>
        </ModalWrapper>
      </ThemeProvider>
    );
  };

  return createPortal(renderModal(), document.body);
};
